// Dependencies
import React, { useEffect, useState, useRef, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';

// Styles
import ModalStyles from './modal.styles';

export default function Modal({
  children,
  modalId,
  header,
  currentModal,
  handleClose,
}) {
  const open = currentModal === modalId;
  const [fadeIn, setFadeIn] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (typeof document !== `undefined` && open) {
        document.body.style.overflow = 'hidden';
      }
    }, 250);

    if (open) {
      setFadeIn(true);
    }

    return () => clearTimeout(timer);
  }, [open, setFadeIn, modalId, currentModal]);

  useLayoutEffect(() => {
    if (typeof document !== `undefined`) {
      document.body.style.overflow = 'initial';
    }
  }, []);

  const close = () => {
    if (typeof window !== 'undefined')
      window.history.replaceState(
        {},
        '',
        `${window.location.pathname}${
          modalId === 'locationPrompt' ? window.location.search : ''
        }`,
      );

    setFadeIn(false);

    setTimeout(() => {
      handleClose();
      if (typeof document !== `undefined`) {
        document.body.style.overflow = 'initial';
      }
    }, 410);
  };

  function useOutsideAlerter(ref) {
    useEffect(() => {
      // Close modal if clicked outside of element
      function handleClickOutside(event) {
        if (ref.current && !ref.current.contains(event.target)) {
          close();
        }
      }

      // Bind event listener
      document.addEventListener('mousedown', handleClickOutside);

      // Unbind event listener on clean-up
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [ref]);
  }

  const wrapperRef = useRef(null);
  useOutsideAlerter(wrapperRef);

  if (!open) return '';

  return (
    <ModalStyles
      modalId={modalId}
      open={open}
      className={`${fadeIn ? 'fade-in' : ''}`}
    >
      <div className="modal-wrapper">
        <div className="modal" ref={wrapperRef}>
          <header className="modal-header">
            {header}

            <button
              type="button"
              className="close"
              aria-label="Close"
              onClick={close}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 320 512"
                fill="#535E6B"
                className="icon"
              >
                <path d="M315.3 411.3c-6.253 6.253-16.37 6.253-22.63 0L160 278.6l-132.7 132.7c-6.253 6.253-16.37 6.253-22.63 0c-6.253-6.253-6.253-16.37 0-22.63L137.4 256L4.69 123.3c-6.253-6.253-6.253-16.37 0-22.63c6.253-6.253 16.37-6.253 22.63 0L160 233.4l132.7-132.7c6.253-6.253 16.37-6.253 22.63 0c6.253 6.253 6.253 16.37 0 22.63L182.6 256l132.7 132.7C321.6 394.9 321.6 405.1 315.3 411.3z" />
              </svg>
            </button>
          </header>
          <div className="modal-body">{children}</div>
        </div>
      </div>
    </ModalStyles>
  );
}

Modal.propTypes = {
  children: PropTypes.node.isRequired,
  modalId: PropTypes.string.isRequired,
  header: PropTypes.node,
  currentModal: PropTypes.string,
  handleClose: PropTypes.func,
};

Modal.defaultProps = {
  header: '',
  currentModal: null,
  handleClose: () => {},
};
